import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './PhoneInput.css';
import { ShortlistProfile } from '../types';
import { faPaperPlane } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  newsletter: boolean;
  privacyAccepted: boolean;
}

interface ContactFormProps {
  onSubmit: (data: ContactFormData) => Promise<string> | void;
  loading?: boolean;
  shortlistedProfiles?: ShortlistProfile[];
}

const ContactForm: React.FC<ContactFormProps> = ({ onSubmit, loading = false, shortlistedProfiles = [] }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    newsletter: false,
    privacyAccepted: false,
  });

  const [errors, setErrors] = useState<Partial<ContactFormData>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Call the original onSubmit function and get the shortlist ID
        const shortlistId = await onSubmit(formData);

        // Redirect to shortlist confirmation page with the ID
        if (shortlistId) {
          navigate(`/shortlists/${shortlistId}`);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        // Handle error if needed
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-2" style={{ color: '#03238C', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#03238C',
              borderColor: errors.name ? '#EF4444' : '#D1D5DB',
              minWidth: '230px'
            }}
            placeholder="Enter your full name"
          />
          {errors.name && (
            <p className="mt-1 text-sm" style={{ color: '#EF4444' }}>{errors.name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: '#03238C', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#03238C',
              borderColor: errors.email ? '#EF4444' : '#D1D5DB',
              minWidth: '230px'
            }}
            placeholder="Enter your email address"
          />
          {errors.email && (
            <p className="mt-1 text-sm" style={{ color: '#EF4444' }}>{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium mb-2" style={{ color: '#03238C', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
            Phone Number *
          </label>
          <PhoneInput
            value={formData.phone}
            onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
            defaultCountry="US"
            international
            countryCallingCodeEditable={false}
            className={`phone-input ${errors.phone ? 'error' : ''}`}
            style={{ minWidth: '230px' }}
            placeholder="Enter your phone number"
          />
          {errors.phone && (
            <p className="mt-1 text-sm" style={{ color: '#EF4444' }}>{errors.phone}</p>
          )}
        </div>

        {/* Company */}
        <div>
          <label htmlFor="company" className="block text-sm font-medium mb-2" style={{ color: '#03238C', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
            Company Name *
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.company ? 'border-red-500' : 'border-gray-300'
              }`}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#03238C',
              borderColor: errors.company ? '#EF4444' : '#D1D5DB',
              minWidth: '230px'
            }}
            placeholder="Enter your company name"
          />
          {errors.company && (
            <p className="mt-1 text-sm" style={{ color: '#EF4444' }}>{errors.company}</p>
          )}
        </div>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium mb-2" style={{ color: '#03238C', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
          Message
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          value={formData.message}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${errors.message ? 'border-red-500' : 'border-gray-300'
            }`}
          style={{
            backgroundColor: '#FFFFFF',
            color: '#03238C',
            borderColor: errors.message ? '#EF4444' : '#D1D5DB',
            minWidth: '230px'
          }}
          placeholder="Tell us about your project requirements, timeline, and any specific needs..."
        />
        {errors.message && (
          <p className="mt-1 text-sm" style={{ color: '#EF4444' }}>{errors.message}</p>
        )}
      </div>

      {/* Checkboxes */}
      <div className="space-y-4">
        {/* Newsletter Checkbox */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="newsletter"
            name="newsletter"
            checked={formData.newsletter}
            onChange={handleInputChange}
            className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="newsletter" className="text-sm" style={{ color: '#03238C', fontFamily: 'Source Sans 3, sans-serif' }}>
            I would like to be kept up to date with news and updates from DreamDev
          </label>
        </div>

        {/* Privacy Policy Checkbox */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="privacyAccepted"
            name="privacyAccepted"
            checked={formData.privacyAccepted}
            onChange={handleInputChange}
            className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            required
          />
          <label htmlFor="privacyAccepted" className="text-sm" style={{ color: '#03238C', fontFamily: 'Source Sans 3, sans-serif' }}>
            I agree to allow DreamDev to store and process my personal data as outlined in our{' '}
            <a
              href="https://dreamdev.io/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
              style={{ color: '#0034E6' }}
            >
              Privacy Policy
            </a>
          </label>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading || !formData.privacyAccepted}
          className="px-4 py-2 text-sm font-bold uppercase flex items-center justify-center space-x-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'rgba(0, 52, 230, 0.1)', // #0034E6 at 10% opacity
            color: '#0034E6', // Blue text
            fontSize: 'var(--dreamdev-font-size-sm)',
            fontWeight: '700',
            borderRadius: '6px',
            border: '1px solid #0034E6', // Blue border
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            if (!loading) (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(0, 52, 230, 0.2)'; // Darker blue on hover
          }}
          onMouseLeave={(e) => {
            if (!loading) (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(0, 52, 230, 0.1)'; // Original background on leave
          }}
        >
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4" />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
          )}
          <span>{loading ? 'Sending...' : 'SUBMIT SHORTLIST'}</span>
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
